import React, { useEffect, useState } from 'react'
import DocumentTitle from 'react-document-title'
import { useHistory, useLocation } from 'react-router-dom'
import startCase from 'lodash/startCase'
import toLower from 'lodash/toLower'
import moment from 'moment'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages'
import Button from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { Divider, makeStyles } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { createStyles } from 'transfix-ui/core/styles'
import { IError } from 'types/graphqlTypes'
import { DeleteIcon } from 'transfix-icons'
import { brandColors, IconButton, Spinner, Toast, useToastState } from 'transfix-ui'
import FORMATS from 'constants/datetime'
import DISPLAY from 'constants/display'
import { useCreateCarrierApplicationMutation } from '../CarrierApplicationForm/graphql/CreateCarrierApplicationMutation'
import { DataReportedBy } from '../Compliance/DataReportedBy'
import {
  IFindOnboardingCarrierQuery,
  useFindOnboardingCarrierLazyQuery,
} from './graphql/FindOnboardingCarrier'

const useQueryString = () => {
  const location = useLocation()
  return new URLSearchParams(location.search)
}

const useCarrierSnapshotStyles = makeStyles(() => {
  return createStyles<string, { hasPassedHighwayAssesment?: boolean | null }>({
    divider: {
      marginTop: 10,
    },
    card: {
      backgroundColor: ({ hasPassedHighwayAssesment }) =>
        hasPassedHighwayAssesment ? brandColors.green0 : 'none',
      borderColor: ({ hasPassedHighwayAssesment }) =>
        hasPassedHighwayAssesment ? brandColors.green2 : 'none',
      display: 'flex',
      padding: ({ hasPassedHighwayAssesment }) => (hasPassedHighwayAssesment ? 20 : 0),
      marginTop: ({ hasPassedHighwayAssesment }) => (hasPassedHighwayAssesment ? 0 : 15),
      borderRadius: ({ hasPassedHighwayAssesment }) => (hasPassedHighwayAssesment ? '4px' : '0px'),
      borderWidth: ({ hasPassedHighwayAssesment }) => (hasPassedHighwayAssesment ? '1px' : 'none'),
      borderStyle: ({ hasPassedHighwayAssesment }) =>
        hasPassedHighwayAssesment ? 'solid' : 'none',
      marginBottom: 5,
    },
    cardSection: {
      flex: 1,
    },
    deleteButton: {
      justifyContent: 'center',
      alignItems: 'center',
    },
  })
})

export const CarrierSnapshot = () => {
  const history = useHistory()

  const [mutationErrors, setMutationErrors] = useState<IError[]>([])
  const [motorCarrierData, setMotorCarrierData] = useState<IFindOnboardingCarrierQuery>()
  const queryString = useQueryString()

  const [findOnboardingCarrier, { loading: loadingCarrier }] = useFindOnboardingCarrierLazyQuery({
    onCompleted: setMotorCarrierData,
  })

  const [
    createCarrier,
    { error: createCarrierErrors, loading },
  ] = useCreateCarrierApplicationMutation()

  const { findOnboardingCarrier: carrierInfo } = motorCarrierData || {}

  const {
    addresses,
    contacts,
    continuousAuthorityGrantDate,
    dbaName,
    dotNumber,
    ein,
    legalName,
    mcNumber,
    rulesAssessmentPass,
    safetyRating,
  } = carrierInfo || {}

  const classes = useCarrierSnapshotStyles({ hasPassedHighwayAssesment: rulesAssessmentPass })

  const primaryAddress = addresses?.find(address => address.isType === 'physical')

  const mailingAddress = addresses?.find(address => address.isType === 'mailing')

  const address = primaryAddress || mailingAddress

  const {
    countryCode = DISPLAY.missingHighwayData,
    stateCode = DISPLAY.missingHighwayData,
    street1 = DISPLAY.missingHighwayData,
    postalCode = DISPLAY.missingHighwayData,
    city: cityAddress = DISPLAY.missingHighwayData,
  } = address || {}

  const einNumber = ein || DISPLAY.missingHighwayData
  const street = street1 ? startCase(toLower(street1)) : DISPLAY.missingHighwayData
  const city = cityAddress ? startCase(toLower(cityAddress)) : DISPLAY.missingHighwayData
  const dba = dbaName || DISPLAY.missingHighwayData
  const date = continuousAuthorityGrantDate
    ? moment(continuousAuthorityGrantDate).format(FORMATS.dateFormat)
    : DISPLAY.missingHighwayData
  const contact = contacts?.find(contact => contact.isType === 'dispatch')
  const contactEmail = contact?.emailAddress || DISPLAY.missingHighwayData
  const dotNumberQueryParam = queryString.get('dotNumber') || ''
  const mcNumberQueryParam = queryString.get('mcNumber') || ''
  const rating = safetyRating || DISPLAY.missingHighwayData
  const { closeToast, multiLine, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const submitHandler = async () => {
    const { data } = await createCarrier({
      variables: {
        input: {
          ein,
          contactEmail: contact?.emailAddress,
          dbaName,
          dotNumber: dotNumber?.toString(),
          mcNumber: mcNumber?.toString(),
          name: legalName || '',
          headquarter: {
            address: address?.street1,
            addressLine2: address?.street2,
            city: address?.city,
            contactEmail: contact?.emailAddress,
            contactName: contact?.name,
            contactPhone: contact?.phone,
            countryCode: address?.countryCode,
            stateCode: address?.stateCode,
            zipcode: address?.postalCode,
          },
        },
      },
    })

    const { createCarrierApplication } = data || {}
    const { carrierApplication, errors } = createCarrierApplication || {}
    if (carrierApplication) {
      history.replace(`/${carrierApplication.id}`)
    }
    if (errors) {
      setMutationErrors(errors)
      openToast({
        toastMessage: 'Unable to create carrier at this time. Try again later.',
        toastType: 'alert',
      })
    }
  }

  // get onboarding motor carrier highway data to populate page
  useEffect(() => {
    if (dotNumberQueryParam || mcNumberQueryParam) {
      findOnboardingCarrier({
        variables: {
          dotNumber: parseInt(dotNumberQueryParam, 10),
          mcNumber: parseInt(mcNumberQueryParam, 10),
        },
      })
    }
  }, [findOnboardingCarrier])

  if (loadingCarrier) {
    return <Spinner />
  }

  return (
    <Box className='container-fluid pt8'>
      <DocumentTitle title='New Carrier Application'>
        <Box className='flex flex-column pa6'>
          <Box mb={2}>
            <Typography variant='h2'>New Carrier Application</Typography>
          </Box>
          <Grid container className='mt4' alignItems='center' justifyContent='center'>
            <Grid xs={12} lg={6}>
              <ApolloErrorMessages
                mutationErrors={mutationErrors}
                apolloErrors={createCarrierErrors}
              />
              <Card title='Company Information'>
                <DataReportedBy reporter='highway' />
                <Box className={classes.card}>
                  <Box className={classes.cardSection}>
                    <Box mb={1}>
                      <Typography variant='overline'>
                        {mcNumber ? `DOT ${dotNumber} · MC ${mcNumber}` : `DOT ${dotNumber}`}
                      </Typography>
                    </Box>
                    <Box mb={1}>
                      <Typography variant='subtitle1'>{legalName}</Typography>
                    </Box>
                    {rulesAssessmentPass && (
                      <Box>
                        <Typography variant='subtitle2'>
                          Highway Rules Assessment: Passed
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box className={classes.deleteButton}>
                    <IconButton
                      size='large'
                      name='tertiary'
                      Icon={DeleteIcon}
                      onClick={() => history.goBack()}
                    />
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      DBA Name
                    </Typography>
                    <Typography variant='body2'>{dba}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      EIN
                    </Typography>
                    <Typography variant='body2'>{einNumber}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      In Operation Since
                    </Typography>
                    <Typography variant='body2'>{date}</Typography>
                    <Divider
                      variant='fullWidth'
                      color={brandColors.coolGray2}
                      className={classes.divider}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      Country
                    </Typography>
                    <Typography variant='body2'>{countryCode}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      Address
                    </Typography>
                    <Typography variant='body2'>{street}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      City
                    </Typography>
                    <Typography variant='body2'>{city}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      State
                    </Typography>
                    <Typography variant='body2'>{stateCode}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      ZIP Code
                    </Typography>
                    <Typography variant='body2'>{postalCode}</Typography>
                    <Divider
                      color={brandColors.coolGray2}
                      variant='fullWidth'
                      className={classes.divider}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      Safety
                    </Typography>
                    <Typography variant='body2'>{rating}</Typography>
                    <Divider
                      color={brandColors.coolGray2}
                      variant='fullWidth'
                      className={classes.divider}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color='textSecondary' variant='caption'>
                      Dispatch Email
                    </Typography>
                    <Typography variant='body2'>{contactEmail}</Typography>
                  </Grid>
                </Grid>
                <Toast
                  variation={toastType}
                  open={toastOpen}
                  onClose={closeToast}
                  message={toastMessage}
                  multiLine={multiLine}
                  duration={null}
                />
                <Box mt={3}>
                  <Button onClick={submitHandler} disabled={loading}>
                    Create Carrier
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DocumentTitle>
    </Box>
  )
}
